<script lang="ts">
import { useFormField } from '#core/services/forms'
import { toControlOptions } from '#core/utils/form'
import { clamp } from '#core/utils/number'
import { twMerge } from 'tailwind-merge'
import { HEIGHTS } from './internal/common'

const colors = {
  black: { bg: 'bg-forgd-accent-900', text: 'text-white' },
  red: { bg: 'bg-[#F3E5E5]', text: 'text-[#E02424]' },
  yellow: { bg: 'bg-[#EAE8D1]', text: 'text-[#7D730F]' },
  green: { bg: 'bg-[#C6DAD3]', text: 'text-[#03543F]' },
  gray: { bg: 'bg-[#C7C7BE4D]', text: 'text-[#C7C7BE]' },
  darkGreen: { bg: 'bg-forgd-green-500', text: 'text-white' },
}

const presets = {
  'default': [colors.black, colors.black],
  'red-to-green': [colors.red, colors.yellow, colors.green],
  'green-to-red': [colors.green, colors.yellow, colors.red],
  'red-green': [colors.red, colors.green],
  'green-red': [colors.green, colors.red],
}

type Presets = keyof typeof presets

export interface SwitchProps<T = any> {
  modelValue?: T
  options?: RawControlOptions<T>
  layout?: 'auto' | 'fill' | 'fit' | 'grid'
  size?: 'sm' | 'md' | 'lg'
  colors?: Presets | Colors[]
  wrap?: boolean
}

type Colors = keyof typeof colors
</script>

<script setup lang="ts" generic="T">
defineOptions({
  inheritAttrs: false,
})

/**
 * UiSwitch
 *
 * Flexible switch component supporting binary or multiple options
 * with additional functionality for disabled, icons, colors
 *
 * - pass no options for a No/Yes toggle with boolean output
 * - pass array of ControlOptions, with value, label, icon, disabled, etc
 *
 * Utilizes toControlOptions(); you can also pass:
 *
 * - array of objects, i.e. [{ value, label, ... }, ... ]
 * - object of label:value pairs, i.e. { 'Label': value, ... }
 * - array of string labels, i.e. ['One', 'Two', ... ]
 * - string of pipe-separated labels, i.e. 'One|Two|...'
 *
 * Core, @see https://github.com/forged-com/forgd/pull/2380
 * Slots, @see https://github.com/forged-com/forgd/pull/2386
 * Layout: @see https://github.com/forged-com/forgd/pull/3513
 */
const props = withDefaults(defineProps<SwitchProps<T>>(), {
  layout: 'auto',
  size: 'md',
  wrap: false,
})

const emits = defineEmits([
  'change',
  'update:modelValue',
])

const attrs = useAttrs()

const marker = ref<HTMLDivElement>()

const buttons = ref<HTMLButtonElement[]>([])

// ---------------------------------------------------------------------------------------------------------------------
// state
// ---------------------------------------------------------------------------------------------------------------------

const {
  id: fieldId,
  emitFormChange,
} = useFormField<SwitchProps>(props)

/// models
const options = computed<{ value: T, label: string }[]>(() => {
  return !props.options
    ? toControlOptions({ No: false, Yes: true })
    : toControlOptions(props.options)
})

// track current value independently of props; this allows model-less slots to work
const currentValue = ref<T>(props.modelValue ?? options.value[0].value)
watch(() => props.modelValue, (value) => {
  currentValue.value = value
})

const selectedIndex = computed({
  get() {
    return options.value.findIndex(option => option.value === currentValue.value) || 0
  },
  set(index: number) {
    const option = options.value[index]
    currentValue.value = option.value
    emits('update:modelValue', option.value)
    emits('change', option.value, option)
    emitFormChange()
  },
})

// ---------------------------------------------------------------------------------------------------------------------
// computed properties
// ---------------------------------------------------------------------------------------------------------------------

const isToggle = computed(() => {
  return options.value.length === 2 && options.value.every(item => typeof item.value === 'boolean')
})

const layout = computed(() => {
  if (props.layout === 'auto') {
    return isToggle.value
      ? 'grid' // toggles are always grid
      : fieldId.value
        ? 'fill' // form controls should fill
        : 'grid'
  }

  // otherwise, use the layout prop
  return props.layout
})

// ---------------------------------------------------------------------------------------------------------------------
// appearance
// ---------------------------------------------------------------------------------------------------------------------

const preset = computed(() => {
  return Array.isArray(props.colors)
    ? props.colors?.map(key => colors[key])
    : presets[(props.colors || 'default')]
})

function getColors(index: number): typeof colors[Colors] {
  return preset.value[index] || colors.black
}

const ui = computed(() => {
  // sizes
  const elementHeight = HEIGHTS[props.size]

  // flags
  const index = selectedIndex.value

  // common
  const transition = 'transition-all duration-[200ms] ease-out'

  // width
  const containerWidth = layout.value === 'fill'
    ? 'w-full'
    : 'inline-block'
  const listWidth = attrs.class?.includes('w-')
    ? 'w-full'
    : ''

  // layout
  const listLayout = layout.value === 'grid'
    ? 'inline-grid'
    : layout.value === 'fill'
      ? 'flex w-full'
      : 'flex'
  const buttonLayout = layout.value === 'grid'
    ? 'overflow-hidden'
    : ''

  // color
  const buttonColor = getColors(index)
  const markerBackground = !props.colors && isToggle.value && index === 1
    ? 'bg-forgd-green-500'
    : buttonColor.bg

  return {
    container: twMerge([
      containerWidth,
      attrs.class,
    ]),
    element: [
      elementHeight,
    ],
    list: [
      listLayout,
      listWidth,
    ],
    button: [
      buttonLayout,
      transition,
    ],
    selectedButton: [
      buttonColor.text,
    ],
    marker: [
      markerBackground,
      transition,
    ],
  }
})

// ---------------------------------------------------------------------------------------------------------------------
// marker
// ---------------------------------------------------------------------------------------------------------------------

watch(selectedIndex, () => updateMarker(true))
watch(() => props.options, () => updateMarker())
watch(() => props.layout, () => updateMarker())
watch(() => props.size, () => updateMarker())

onMounted(() => {
  if (import.meta.client) {
    updateMarker()
    window.addEventListener('resize', onResize)
  }
})

onUnmounted(() => {
  if (import.meta.client) {
    window.removeEventListener('resize', onResize)
  }
})

onActivated(() => {
  updateMarker()
})

function onResize() {
  updateMarker(false)
}

function updateMarker(animate = false) {
  if (marker.value) {
    if (!animate) {
      marker.value.style.transition = 'none'
      setTimeout(() => {
        marker.value.style.transition = ''
      })
    }
    nextTick(() => {
      const target = buttons.value[selectedIndex.value]
      if (target) {
        marker.value.style.width = `${target.offsetWidth}px`
        marker.value.style.height = `${target.offsetHeight}px`
        marker.value.style.transform = `translateX(${target.offsetLeft}px)`
      }
    })
  }
}

defineExpose({
  updateMarker,
})

// ---------------------------------------------------------------------------------------------------------------------
// interaction
// ---------------------------------------------------------------------------------------------------------------------

// emulate click functionality for toggles
function onClick(event: MouseEvent) {
  if (isToggle.value) {
    event.preventDefault()
    event.stopPropagation()
    selectedIndex.value = selectedIndex.value === 0
      ? 1
      : 0
  }
}

function focusButton(direction: -1 | 1) {
  const index = clamp(selectedIndex.value + direction, buttons.value)
  if (index !== selectedIndex.value) {
    selectedIndex.value = index
    buttons.value[index].focus()
  }
}
</script>

<template>
  <div
    data-ui="UiSwitch"
    :data-layout="`${props.layout}/${layout}`"
    :class="ui.container"
  >
    <div :class="ui.element">
      <div
        class=" relative items-stretch
                w-auto h-full p-[5px] rounded-lg
              bg-forgd-neutral-600/10 ring-1 ring-forgd-neutral-600 ring-inset
                overflow-hidden cursor-pointer
        "
        :class="ui.list"
        :style="layout === 'grid'
          ? `grid-template-columns: repeat(${options.length}, minmax(0, 1fr))`
          : ''
        "
        role="tablist"
        @click.capture="onClick"
      >
        <div
          ref="marker"
          class=" absolute top-[5px] left-0
                  h-full rounded-md
                  shadow-md
          "
          :class="ui.marker"
        />
        <button
          v-for="(option, index) in options"
          :key="option.value"
          ref="buttons"
          class=" relative
                  flex flex-1 items-center justify-center
                  h-full gap-2 px-3 rounded-md
                  text-xs font-semibold
          "
          :class="[
            ui.button,
            option.disabled
              ? 'disabled:cursor-not-allowed'
              : '',
            option.disabled
              ? 'text-neutral-900/30'
              : selectedIndex === index
                ? ui.selectedButton
                : 'text-secondary hover:text-primal',
          ]"
          :disabled="option.disabled"
          :tabindex="selectedIndex === index ? 0 : -1"
          role="tab"
          @click="selectedIndex = index"
          @keydown.left="focusButton(-1)"
          @keydown.right="focusButton(1)"
        >
          <slot v-if="option.icon" name="icon" v-bind="option">
            <UiIcon v-if="option.icon" :name="option.icon" />
          </slot>
          <slot name="label" v-bind="option">
            <span :class="{ truncate: !wrap }">{{ option.label }}</span>
          </slot>
        </button>
      </div>
    </div>
    <div>
      <slot
        v-bind="{
          index: selectedIndex,
          value: currentValue,
          option: options[selectedIndex],
        }"
      />
    </div>
  </div>
</template>
